'use client';

import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { FC } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { ChevronRight, MailOutline, PhoneFilled } from '@eqi-investimentos/icons/web';
import { Box, Button, Flex, Skeleton, Spinner, Stack, Text } from '@eqi-investimentos/react';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useCreateSFOpportunity } from '@/modules/Customer/http/client/mutations/useCreateSFOpportunity';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { useGetCustomerAdvisor } from '@/modules/Customer/http/client/useGetCustomerAdvisor';
import { Icon } from '@/components/atoms/Icon';
import { Thumb } from '@/components/atoms/Thumb';
import { IfRender } from '@/utils/jsx';
import { formatPhoneNumber } from '@/utils/masks';
export const CardMyAdvisor: FC<CardMyAdvisorProps> = ({
  hasAdvisor = false,
  opportunity = {
    action: '',
    product: ''
  },
  variant = 'desktop'
}) => {
  const {
    t
  } = useTranslation('common');
  const pathname = usePathname();
  const isMyAdvisoryScreen = pathname.startsWith('/minha-assessoria');
  const disableHref = isMyAdvisoryScreen || hasAdvisor;
  const {
    mutate: sendClickEvent
  } = useSendClickEvent();
  const {
    mutate: createSFOpportunity,
    isPending
  } = useCreateSFOpportunity();
  const {
    data: advisor,
    isFetching
  } = useGetCustomerAdvisor();
  const handleOpportunity = async () => {
    if (!opportunity || isPending) return;
    createSFOpportunity(opportunity);
    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.myAdvisor.label('/minha-assessoria')
    });
  };
  if (isFetching) {
    return <Skeleton w="full" h="8.5625rem" borderRadius="base" />;
  }
  if (!advisor?.advisorName) {
    return <></>;
  }
  if (variant === 'mobile') {
    return <Flex p="m" background="background.brandLight" w="full" justify="space-between" align="center" cursor="pointer" onClick={handleOpportunity} position="relative" borderRadius="xs" pointerEvents={isPending ? 'none' : 'auto'}>
        <IfRender condition={isPending}>
          <Flex background="transparency.high" h="full" w="full" position="absolute" left={0} top={0} align="center" justify="center" borderRadius="xs" cursor="not-allowed">
            <Spinner zIndex={99} />
          </Flex>
        </IfRender>
        <Flex gap="s">
          <Thumb src={advisor.photo || '/assets/images/placeholder_my_advisor.svg'} width={48} height={48} alt={advisor.photo ? 'foto do assessor' : 'icone de um atentender com headphone'} />
          <Stack flexDir="column" gap="3xs">
            <Text size="l" fontWeight="light" color="content.primary" whiteSpace="nowrap">
              {isMyAdvisoryScreen ? t('screens.myAdvisory.speakWithAdvisor') : t('attributes.myConsultancy')}
            </Text>
            <Text size="xl" color="content.primary" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {advisor.advisorName}
            </Text>
          </Stack>
        </Flex>
        <Icon iconName="ChevronRight" color="content.brand" w={24} h={24} />
      </Flex>;
  }
  return <Flex flexDir="column" gap="0.9375rem" p="m" w="full" bg="background.brandLight" borderRadius="lg" overflow="hidden" data-sentry-element="Flex" data-sentry-component="CardMyAdvisor" data-sentry-source-file="index.tsx">
      <Flex as={NextLink} href={disableHref ? '#' : '/minha-assessoria'} cursor={disableHref ? 'initial' : 'pointer'} w="full" mb="4" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <Flex w="full" justifyContent="space-between" alignItems="center" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
          <Flex alignItems="center" gap="2.5" maxW="calc(100% - 1.5rem)" sx={{
          '.chakra-avatar': {
            h: '3.125rem',
            w: '3.125rem',
            '--avatar-font-size': '1.25rem',
            color: 'white'
          }
        }} data-sentry-element="Flex" data-sentry-source-file="index.tsx">
            <Thumb src={advisor.photo || '/assets/images/placeholder_my_advisor.svg'} width={48} height={48} alt={advisor.photo ? 'foto do assessor' : 'icone de um atentender com headphone'} data-sentry-element="Thumb" data-sentry-source-file="index.tsx" />
            <Flex flexDir="column" gap="0.3125rem" maxW="calc(100% - 4rem)" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
              <Text fontSize="sm" lineHeight="5" color="content.primary" whiteSpace="nowrap" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                {isMyAdvisoryScreen ? t('screens.myAdvisory.speakWithAdvisor') : t('attributes.myConsultancy')}
              </Text>
              <Text fontSize="sm" lineHeight="5" color="content.primary" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                {advisor.advisorName}
              </Text>
            </Flex>
          </Flex>
          <IfRender condition={!isMyAdvisoryScreen && hasAdvisor} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
            <ChevronRight w={24} h={24} color="action.active.brand" data-sentry-element="ChevronRight" data-sentry-source-file="index.tsx" />
          </IfRender>
        </Flex>
      </Flex>
      <Flex display={{
      base: 'none',
      lg: 'flex'
    }} justifyContent="space-between" alignItems="center" gap="6" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <Flex flexDir="column" gap="2.5" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
          <Text display="inline-flex" gap="2.5" fontSize="sm" lineHeight="0.875rem" color="content.secondary" data-sentry-element="Text" data-sentry-source-file="index.tsx">
            <MailOutline w={16} h={16} color="content.brand" data-sentry-element="MailOutline" data-sentry-source-file="index.tsx" />
            {advisor.advisorEmail}
          </Text>
          <Text display="inline-flex" gap="2.5" fontSize="sm" lineHeight="0.875rem" color="content.secondary" data-sentry-element="Text" data-sentry-source-file="index.tsx">
            <PhoneFilled w={16} h={16} color="content.brand" data-sentry-element="PhoneFilled" data-sentry-source-file="index.tsx" />
            {formatPhoneNumber(advisor.advisorMobilePhone ?? '')}
          </Text>
        </Flex>
        <Button variant="solid" onClick={handleOpportunity} display="inline-flex" justifyContent="center" alignItems="center" gap="2.5" p="2.5" w="6.5rem" h="9" fontSize="xs" lineHeight="0.875rem" cursor="pointer" borderRadius="0.3125rem" zIndex="docked" data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {t('attributes.toTalk')}
        </Button>
      </Flex>
    </Flex>;
};
type CardMyAdvisorProps = {
  hasAdvisor?: boolean;
  opportunity?: {
    action: string;
    product?: string;
    description?: string;
  };
  variant?: 'desktop' | 'mobile';
};